<template>
  <div class="content mt-consignment-payments-page">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.consignment_payment.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <!-- ปุ่มเขียน: ซ่อนทั้งปุ่มเมื่อสิทธิ์ Modern Trade เป็นอ่านอย่างเดียว -->
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="sm" type="neutral" @click="openRecord()">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.consignment_payment.record") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- ═══ การ์ดสรุปของบริษัทที่เลือก (ไม่เลือก = รวมทุกบริษัท) ═══ -->
      <div class="row" v-loading="summaryLoading">
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.total_received')"
            type="gradient-info"
            :sub-title="formatMoney(summary.total_received)"
            icon="ni ni-money-coins"
          >
            <template slot="footer">
              <span class="text-nowrap">{{ summaryScopeLabel }}</span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.unlinked_amount')"
            type="gradient-orange"
            :sub-title="formatMoney(summary.unlinked_amount)"
            icon="ni ni-collection"
          >
            <template slot="footer">
              <span class="text-nowrap">
                {{ $t("modern_trade.consignment_payment.last_paid_date") }}:
                {{ formatDate(summary.last_paid_date) }}
              </span>
            </template>
          </stats-card>
        </div>
        <div class="col-xl-4 col-md-6">
          <stats-card
            :title="$t('modern_trade.consignment_payment.unbilled_sales_amount')"
            type="gradient-green"
            :sub-title="formatMoney(summary.issuable_amount)"
            icon="ni ni-chart-bar-32"
          >
            <template slot="footer">
              <span class="text-nowrap text-muted">
                {{ $t("modern_trade.consignment_payment.unbilled_hint") }}
              </span>
              <!-- เพดานนี้ "หด" เมื่อมีสินค้าที่ยังไม่ผูก/ไม่มีราคา (ยอดขายของมันนับไม่ได้)
                   ต้องอธิบายตรงจุดที่โชว์ตัวเลข ไม่ใช่รอให้ออกใบไม่ผ่านก่อน -->
              <span
                v-if="summary.skipped_unpriced > 0"
                class="badge badge-warning mt-csg-badge ml-2"
                :title="$t('modern_trade.consignment_payment.skipped_unpriced_card_hint', { count: summary.skipped_unpriced })"
              >
                {{ $t("modern_trade.consignment_payment.skipped_unpriced_badge", { count: summary.skipped_unpriced }) }}
              </span>
            </template>
          </stats-card>
        </div>
      </div>

      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <h3 class="mb-0">{{ $t("modern_trade.consignment_payment.title") }}</h3>
          <p class="text-sm text-muted mb-0">
            {{ $t("modern_trade.consignment_payment.subtitle") }}
          </p>
        </template>

        <!-- ═══ ตัวกรอง ═══ -->
        <div class="row align-items-end mb-3 px-4">
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.company") }}</label>
            <el-select
              v-model="filters.company_id"
              :placeholder="$t('modern_trade.consignment_payment.all_companies')"
              clearable
              class="w-100"
              @change="onFilter"
            >
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.date_from") }}</label>
            <el-date-picker
              v-model="filters.date_from"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-2">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.date_to") }}</label>
            <el-date-picker
              v-model="filters.date_to"
              type="date"
              value-format="yyyy-MM-dd"
              clearable
              class="w-100"
              @change="onFilter"
            ></el-date-picker>
          </div>
          <div class="col-md-3">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.link_status") }}</label>
            <el-select v-model="filters.link_status" class="w-100" @change="onFilter">
              <el-option value="" :label="$t('modern_trade.consignment_payment.status_all')"></el-option>
              <el-option value="unlinked" :label="$t('modern_trade.consignment_payment.status_unlinked')"></el-option>
              <el-option value="linked" :label="$t('modern_trade.consignment_payment.status_linked')"></el-option>
            </el-select>
          </div>
          <div class="col-md-2">
            <el-button
              type="primary"
              icon="el-icon-search"
              class="w-100 mt-csg-search-btn"
              @click="onFilter"
            >{{ $t("search") }}</el-button>
          </div>
        </div>

        <!-- ═══ แถบเลือก + ออกใบกำกับภาษีจากเงินที่เลือก (spec 2026-08-13 รอบบ่าย) ═══
             ใบกำกับออกตาม "เงินที่รับจริง" — ผู้ใช้ติ๊กเลือกรายการรับเงินที่ยังไม่ผูกใบ
             ของบริษัทเดียวกัน แล้ว BE ออกใบยอดเท่ากับเงินที่เลือกพอดี
             ทั้งแถบเป็นการเขียน → ซ่อนทั้งแถบเมื่อสิทธิ์เป็นอ่านอย่างเดียว -->
        <div v-if="isEditModernTrade" class="px-4 mb-3 mt-csg-selection-bar">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="mt-csg-selection-figures">
              <span class="mr-3">
                {{ $t("modern_trade.consignment_payment.selected_count", { count: selectedRows.length }) }}
              </span>
              <span class="mr-3">
                {{ $t("modern_trade.consignment_payment.selected_amount") }}:
                <strong class="text-success">{{ formatMoney(selectedAmount) }}</strong>
              </span>
              <span v-if="selectedCompanySummary">
                {{ $t("modern_trade.consignment_payment.selectable_sales") }}:
                <strong>{{ formatMoney(selectedCompanySales) }}</strong>
                <span
                  v-if="selectedCompanySkipped > 0"
                  class="badge badge-warning mt-csg-badge ml-1"
                  :title="$t('modern_trade.consignment_payment.skipped_unpriced_card_hint', { count: selectedCompanySkipped })"
                >
                  {{ $t("modern_trade.consignment_payment.skipped_unpriced_badge", { count: selectedCompanySkipped }) }}
                </span>
              </span>
            </div>
            <el-button
              type="success"
              size="small"
              :disabled="!canIssueSelected"
              :loading="issuing"
              @click="issueSelected"
            >
              <i class="fa fa-file-invoice"></i>
              {{ $t("modern_trade.consignment_payment.issue_selected") }}
            </el-button>
          </div>
          <!-- เหตุผลที่ปุ่มกดไม่ได้ต้องมองเห็น ไม่ใช่เงียบ ๆ (ข้ามบริษัท / ยังไม่เลือก) -->
          <p class="small mb-0 mt-1" :class="selectionNotice.cls">{{ selectionNotice.text }}</p>
        </div>

        <!-- ═══ ตารางเงินรับฝากขาย ═══ -->
        <div v-loading="loading">
          <el-table
            ref="paymentsTable"
            :data="rows"
            row-key="id"
            header-row-class-name="thead-light"
            @selection-change="onSelectionChange"
          >
            <!-- เลือกได้เฉพาะแถวที่ยังไม่ผูกใบกำกับ (selectable) -->
            <el-table-column
              v-if="isEditModernTrade"
              type="selection"
              width="45"
              :selectable="isRowSelectable"
            ></el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.paid_date')" min-width="110">
              <template slot-scope="{ row }">{{ formatDate(row.paid_date) }}</template>
            </el-table-column>
            <!-- เงินเก่าที่ company_id เป็น NULL: BE ตั้งใจโชว์ไว้ให้เห็นเพื่อไปแก้
                 (ผูกใบก็ไม่ได้ ออกใบก็ไม่ได้) — ต้องบอกผู้ใช้ว่าทำไมติ๊กไม่ได้ -->
            <el-table-column :label="$t('modern_trade.consignment_payment.company')" min-width="180">
              <template slot-scope="{ row }">
                <template v-if="row.company_id">{{ row.company_name || row.company_id }}</template>
                <template v-else>
                  <span class="text-muted">{{ $t("modern_trade.consignment_payment.no_company") }}</span>
                  <small class="d-block text-muted">
                    {{ $t("modern_trade.consignment_payment.no_company_hint") }}
                  </small>
                </template>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.amount')" min-width="130" align="right">
              <template slot-scope="{ row }">{{ formatMoney(row.amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.method')" min-width="110">
              <template slot-scope="{ row }">{{ methodLabel(row.method) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.slip')" min-width="80" align="center">
              <template slot-scope="{ row }">
                <a v-if="row.slip_s3_key" :href="slipUrl(row.slip_s3_key)" target="_blank" rel="noopener">
                  <i class="fa fa-paperclip"></i>
                </a>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.invoice')" min-width="150">
              <template slot-scope="{ row }">
                <span v-if="row.invoice_id" class="badge badge-success mt-csg-badge">
                  {{ row.invoice_doc_no || "#" + row.invoice_id }}
                </span>
                <span v-else class="badge badge-warning mt-csg-badge">
                  {{ $t("modern_trade.consignment_payment.pending_invoice") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.note')" min-width="150">
              <template slot-scope="{ row }">{{ row.note || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.consignment_payment.created_by')" min-width="130">
              <template slot-scope="{ row }">{{ row.created_by_name || "-" }}</template>
            </el-table-column>
            <!-- ทั้งคอลัมน์หายไปเมื่อผู้ใช้มีสิทธิ์อ่านอย่างเดียว -->
            <el-table-column
              v-if="isEditModernTrade"
              :label="$t('actions')"
              width="90"
              align="center"
            >
              <template slot-scope="{ row }">
                <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                  <base-button size="sm" type="secondary" class="btn-icon-only" :title="$t('actions')">
                    <i class="fa fa-ellipsis-v"></i>
                  </base-button>
                  <el-dropdown-menu slot="dropdown">
                    <!-- แถวไร้บริษัท: MtPaymentLink ตอบ 409 payment_no_company เสมอ
                         → ปิดปุ่มพร้อมบอกเหตุผล ไม่ปล่อยให้กดแล้วไปตายที่ BE -->
                    <el-dropdown-item command="link" :disabled="!!row.invoice_id || !row.company_id">
                      <i class="fa fa-link text-info mr-2"></i>{{ $t("modern_trade.consignment_payment.link") }}
                      <small v-if="!row.company_id" class="d-block text-muted">
                        {{ $t("modern_trade.consignment_payment.no_company_short") }}
                      </small>
                    </el-dropdown-item>
                    <el-dropdown-item command="unlink" :disabled="!row.invoice_id">
                      <i class="fa fa-unlink text-warning mr-2"></i>{{ $t("modern_trade.consignment_payment.unlink") }}
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" divided>
                      <span class="text-danger"><i class="fa fa-trash mr-2"></i>{{ $t("delete") }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
          <p v-if="!loading && rows.length === 0" class="text-muted text-center py-4 mb-0">
            {{ $t("modern_trade.consignment_payment.no_data") }}
          </p>
        </div>

        <div class="d-flex align-items-center justify-content-between mt-3 px-4">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadList"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- ═══ Dialog: บันทึกรับเงิน (ไม่มีเพดาน — LOCKED) ═══ -->
    <el-dialog
      :title="$t('modern_trade.consignment_payment.record_title')"
      :visible.sync="showRecordDialog"
      width="640px"
      @closed="resetRecordForm"
    >
      <div class="alert alert-info py-2">
        <i class="fa fa-info-circle"></i> {{ $t("modern_trade.consignment_payment.no_cap_hint") }}
      </div>
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.company") }} *</label>
            <el-select
              v-model="recordForm.company_id"
              :placeholder="$t('modern_trade.consignment_payment.company')"
              filterable
              class="w-100"
              @change="loadRecordCompare"
            >
              <el-option
                v-for="c in companies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              ></el-option>
            </el-select>
          </div>
        </div>
        <!-- ตัวเลขเทียบให้เห็น (ไม่ใช่ guard) -->
        <div class="col-12" v-if="recordForm.company_id">
          <div class="row mb-3">
            <div class="col-6">
              <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.unlinked_amount") }}</small>
              <strong>{{ formatMoney(recordCompare.unlinked_amount) }}</strong>
            </div>
            <div class="col-6 text-right">
              <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.unbilled_sales_amount") }}</small>
              <strong>{{ formatMoney(recordCompare.issuable_amount) }}</strong>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.amount") }} *</label>
            <input
              v-model.number="recordForm.amount"
              class="form-control"
              type="number"
              min="0"
              step="0.01"
              @focus="$event.target.select()"
            />
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.paid_date") }} *</label>
            <el-date-picker
              v-model="recordForm.paid_date"
              type="date"
              value-format="yyyy-MM-dd"
              class="w-100"
            ></el-date-picker>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.method") }}</label>
            <el-select v-model="recordForm.method" class="w-100">
              <el-option value="cash" :label="$t('modern_trade.payment.method_cash')"></el-option>
              <el-option value="transfer" :label="$t('modern_trade.payment.method_transfer')"></el-option>
              <el-option value="cheque" :label="$t('modern_trade.payment.method_cheque')"></el-option>
              <el-option value="other" :label="$t('modern_trade.payment.method_other')"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.note") }}</label>
            <input v-model="recordForm.note" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.consignment_payment.slip") }}</label>
            <div>
              <input
                ref="slipInput"
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style="display:none"
                @change="onSlipPicked"
              />
              <el-button size="small" :loading="slipUploading" @click="$refs.slipInput.click()">
                <i class="fa fa-upload"></i> {{ $t("modern_trade.payment.slip_choose") }}
              </el-button>
              <span v-if="recordForm.slip_object_key" class="ml-2">
                <a :href="recordForm.slip_cdn_url" target="_blank" rel="noopener">
                  <i class="fa fa-paperclip"></i> {{ $t("modern_trade.payment.slip_view") }}
                </a>
                <i class="fa fa-times text-danger ml-2" style="cursor:pointer" @click="clearSlip"></i>
              </span>
            </div>
            <small class="text-muted d-block mt-1">{{ $t("modern_trade.payment.slip_hint") }}</small>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showRecordDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitRecord">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- ═══ Dialog: ผูกใบกำกับภาษี ═══ -->
    <el-dialog
      :title="$t('modern_trade.consignment_payment.link_title')"
      :visible.sync="showLinkDialog"
      width="640px"
      @closed="resetLinkForm"
    >
      <div v-if="linkPayment">
        <div class="row mb-3">
          <div class="col-6">
            <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.company") }}</small>
            <strong>{{ linkPayment.company_name || linkPayment.company_id }}</strong>
          </div>
          <div class="col-6 text-right">
            <small class="text-muted d-block">{{ $t("modern_trade.consignment_payment.link_amount") }}</small>
            <strong class="text-success">{{ formatMoney(linkPayment.amount) }}</strong>
          </div>
        </div>
        <p class="text-muted small">{{ $t("modern_trade.consignment_payment.link_hint") }}</p>
        <div v-loading="linkLoading">
          <el-select
            v-model="linkInvoiceId"
            :placeholder="$t('modern_trade.consignment_payment.invoice')"
            class="w-100"
            v-if="linkInvoices.length"
          >
            <el-option
              v-for="iv in linkInvoices"
              :key="iv.id"
              :label="invoiceOptionLabel(iv)"
              :value="iv.id"
              :disabled="invoiceRemaining(iv) < Number(linkPayment.amount || 0)"
            ></el-option>
          </el-select>
          <p v-else-if="!linkLoading" class="text-muted mb-0">
            {{ $t("modern_trade.consignment_payment.link_none") }}
          </p>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showLinkDialog = false">{{ $t("close") }}</el-button>
        <el-button
          type="primary"
          :loading="linking"
          :disabled="!linkInvoiceId"
          @click="submitLink"
        >{{ $t("modern_trade.consignment_payment.link") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * รับเงินฝากขาย — spec docs/specs/2026-08-13-consignment-invoice-from-money.md
 *
 * ความจริงของธุรกิจ: คู่ค้าโอนเงินมาก่อนตามยอดที่ขายได้ → บันทึกไว้ผูกกับ "บริษัท"
 * (sale_order_id NULL + invoice_id NULL = กองรอออกใบ)
 *
 * ออกใบกำกับภาษี = money-driven (แก้ทิศจากของเดิมที่ออกตามรอบ import):
 * ผู้ใช้ติ๊กเลือกรายการรับเงินที่ยังไม่ผูกใบของบริษัทเดียวกัน → POST /consignment-invoices
 * → ใบมียอดสุทธิเท่ากับเงินที่เลือกพอดี รายการสินค้าดึงจากยอดขายจริงที่ยังไม่ออกใบ
 * (เก่าก่อน บรรทัดท้ายเป็นเศษ) ถ้ายอดขายไม่พอ BE ตอบ 400 insufficient_sales
 * พร้อมยอดสูงสุดที่ออกได้ → หน้านี้แสดงให้ผู้ใช้ปรับการเลือก
 *
 * หน้านี้ไม่มีเพดานตอนบันทึกรับเงิน (LOCKED) — การ์ด "ยอดขายที่ยังออกใบได้"
 * เป็นตัวเลขเทียบ (เพดานจริงตัดสินที่ BE ตอนออกใบ)
 */
import { Table, TableColumn, Select, Option, Dialog, Button, DatePicker, Dropdown, DropdownMenu, DropdownItem } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, StatsCard } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtConsignmentPayments",
  components: {
    RouteBreadCrumb,
    BasePagination,
    StatsCard,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // เลือกบริษัท = การ์ดของบริษัทนั้น / ไม่เลือก = รวมทุกบริษัทที่มีความเคลื่อนไหว
    // issuable_amount = ตัวเลขเดียวที่หน้าจอโชว์เป็น "ยอดขายที่ยังออกใบได้" (รวม VAT)
    summary() {
      const empty = {
        total_received: 0,
        unlinked_amount: 0,
        issuable_amount: 0,
        skipped_unpriced: 0,
        last_paid_date: null,
      };
      if (!this.summaryRows.length) return empty;
      if (this.filters.company_id) {
        const row = this.summaryRows.find(s => Number(s.company_id) === Number(this.filters.company_id));
        if (!row) return empty;
        return Object.assign({}, row, {
          issuable_amount: this.issuableAmount(row),
          skipped_unpriced: Number(row.skipped_unpriced) || 0,
        });
      }
      return this.summaryRows.reduce((acc, s) => {
        acc.total_received += Number(s.total_received) || 0;
        acc.unlinked_amount += Number(s.unlinked_amount) || 0;
        acc.issuable_amount += this.issuableAmount(s);
        acc.skipped_unpriced += Number(s.skipped_unpriced) || 0;
        if (s.last_paid_date && (!acc.last_paid_date || s.last_paid_date > acc.last_paid_date)) {
          acc.last_paid_date = s.last_paid_date;
        }
        return acc;
      }, Object.assign({}, empty));
    },
    summaryScopeLabel() {
      if (!this.filters.company_id) return this.$t("modern_trade.consignment_payment.summary_all_hint");
      const c = this.companies.find(x => Number(x.id) === Number(this.filters.company_id));
      return (c && c.name) || this.$t("modern_trade.consignment_payment.company");
    },
    // ตัวเลขเทียบใน dialog บันทึกรับเงิน (ของบริษัทที่เลือกใน dialog)
    recordCompare() {
      const empty = { unlinked_amount: 0, issuable_amount: 0 };
      if (!this.recordForm.company_id) return empty;
      const row = this.summaryRows.find(s => Number(s.company_id) === Number(this.recordForm.company_id));
      if (!row) return empty;
      return Object.assign({}, row, { issuable_amount: this.issuableAmount(row) });
    },

    // ── การเลือกเพื่อออกใบกำกับภาษี ─────────────────────────────────────────────
    selectedAmount() {
      return this.selectedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    },
    // บริษัทที่ปรากฏในรายการที่เลือก — มากกว่า 1 = เลือกข้ามบริษัท (ออกใบไม่ได้)
    selectedCompanyIds() {
      const ids = [];
      this.selectedRows.forEach(r => {
        const id = Number(r.company_id);
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    },
    crossCompanySelection() {
      return this.selectedCompanyIds.length > 1;
    },
    selectedCompanyId() {
      return this.selectedCompanyIds.length === 1 ? this.selectedCompanyIds[0] : null;
    },
    selectedCompanyName() {
      if (!this.selectedCompanyId) return "";
      const row = this.selectedRows.find(r => Number(r.company_id) === this.selectedCompanyId);
      if (row && row.company_name) return row.company_name;
      const c = this.companies.find(x => Number(x.id) === this.selectedCompanyId);
      return (c && c.name) || "#" + this.selectedCompanyId;
    },
    // ยอดขายที่ยังออกใบได้ของบริษัทที่เลือก — null = ยังไม่รู้ (ห้ามเดาเป็น 0 แล้วเตือนผิด)
    selectedCompanySummary() {
      if (!this.selectedCompanyId) return null;
      return this.summaryRows.find(s => Number(s.company_id) === this.selectedCompanyId) || null;
    },
    // เทียบกับ "เงินที่ติ๊กเลือก" ได้ตรง ๆ เพราะเป็นยอดรวม VAT เหมือนกัน
    selectedCompanySales() {
      return this.issuableAmount(this.selectedCompanySummary);
    },
    // > 0 = เพดานข้างบนต่ำกว่ายอดขายจริง เพราะมีสินค้าที่ยังไม่ผูก/ไม่มีราคา
    selectedCompanySkipped() {
      const s = this.selectedCompanySummary;
      return (s && Number(s.skipped_unpriced)) || 0;
    },
    // selectedCompanyId ต้องมีจริง — ไม่งั้น POST จะส่ง company_id: null ออกไป
    // (แถวไร้บริษัทถูกกันที่ isRowSelectable แล้ว นี่คือด่านสอง)
    canIssueSelected() {
      return (
        this.isEditModernTrade &&
        this.selectedRows.length > 0 &&
        !this.crossCompanySelection &&
        !!this.selectedCompanyId &&
        !this.issuing
      );
    },
    // ข้อความใต้ปุ่ม — ปุ่มที่กดไม่ได้ต้องบอกเหตุผลเสมอ (ห้ามเงียบ)
    selectionNotice() {
      if (this.crossCompanySelection) {
        return { cls: "text-danger", text: this.$t("modern_trade.consignment_payment.issue_cross_company") };
      }
      if (!this.selectedRows.length) {
        return { cls: "text-muted", text: this.$t("modern_trade.consignment_payment.issue_hint") };
      }
      // เลือกอยู่แต่ไม่มีบริษัท = แถวไร้บริษัทล้วน (ปกติติ๊กไม่ได้อยู่แล้ว) — ห้ามปล่อย
      // ให้ข้อความบอกว่า "พร้อมออกใบ" ทั้งที่ปุ่มกดไม่ได้
      if (!this.selectedCompanyId) {
        return { cls: "text-danger", text: this.$t("modern_trade.consignment_payment.no_company_hint") };
      }
      // เกินยอดขายที่รองรับ = เตือน (ไม่บล็อก — BE เป็นคนตัดสินด้วยเลขล่าสุด)
      if (this.selectedCompanySummary && this.selectedAmount > this.selectedCompanySales + 0.005) {
        return {
          cls: "text-warning",
          text: this.$t("modern_trade.consignment_payment.issue_over_sales", {
            amount: this.formatMoney(this.selectedCompanySales),
          }),
        };
      }
      return { cls: "text-muted", text: this.$t("modern_trade.consignment_payment.issue_ready") };
    },
  },
  data() {
    return {
      companies: [],
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      filters: {
        company_id: null,
        date_from: null,
        date_to: null,
        link_status: "",
      },
      summaryLoading: false,
      summaryRows: [],
      // ออกใบกำกับจากเงินที่เลือก
      selectedRows: [],
      issuing: false,
      // บันทึกรับเงิน
      showRecordDialog: false,
      saving: false,
      slipUploading: false,
      recordForm: {
        company_id: null,
        amount: null,
        paid_date: null,
        method: "transfer",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      },
      // ผูกใบกำกับ
      showLinkDialog: false,
      linkPayment: null,
      linkInvoices: [],
      linkInvoiceId: null,
      linkLoading: false,
      linking: false,
    };
  },
  mounted() {
    this.loadCompanies();
    this.loadSummary();
    this.loadList();
  },
  methods: {
    // BE ห่อผลลัพธ์เป็น { Data: [ { total, items } ] } (r.Append ของ object)
    // แต่บาง endpoint append ทีละแถว → Data เป็นอาเรย์ของแถวตรง ๆ. รองรับทั้งสองแบบ
    parseList(res) {
      const body = (res && res.data) || {};
      const arr = Array.isArray(body.Data) ? body.Data : [];
      const first = arr[0] || {};
      if (first && Array.isArray(first.items)) {
        return { items: first.items, total: Number(first.total) || first.items.length };
      }
      if (first && Array.isArray(first.data)) {
        return { items: first.data, total: Number(first.total) || first.data.length };
      }
      return { items: arr, total: Number(body.Total) || arr.length };
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = this.parseList(res);
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadSummary() {
      this.summaryLoading = true;
      try {
        const params = {};
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        const res = await ModernTradeService.consignmentPaymentsSummary(params);
        const d = this.parseList(res);
        this.summaryRows = d.items || [];
      } catch (err) {
        this.summaryRows = [];
      } finally {
        this.summaryLoading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadSummary();
      this.loadList();
    },
    async loadList() {
      this.loading = true;
      // ข้อมูลชุดใหม่ = การเลือกเดิมใช้ไม่ได้แล้ว (ไม่เก็บข้ามหน้า/ข้ามตัวกรอง)
      this.clearSelection();
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filters.company_id) params.company_id = this.filters.company_id;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;
        if (this.filters.link_status) params.link_status = this.filters.link_status;
        const res = await ModernTradeService.consignmentPaymentsList(params);
        const d = this.parseList(res);
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loading = false;
      }
    },

    // "ยอดขายที่ยังออกใบได้" ของแถว summary หนึ่งบริษัท
    //
    // BE ส่งมา 2 ตัวจากฐานเดียวกัน: unbilled_sales_amount = ฐานภาษี (ไม่รวม VAT)
    // ส่วน issuable_amount_incl_vat = ตัวเดียวกันบวก VAT กลับด้วยอัตราของบริษัทนั้น
    // หน้าจอนี้เทียบกับ "เงินที่คู่ค้าโอนมา" ซึ่งเป็นยอดรวม VAT จึงต้องใช้ตัวหลัง
    // (ตัวเดียวกับ max_amount ที่ insufficient_sales ตอบกลับมา — เลขก่อน/หลังโดนปฏิเสธ
    // จะได้ตรงกัน) — ใช้ตัวไม่รวม VAT จะเตือน "เกินยอด" ผิดตั้งแต่ยังไม่เกินจริง
    //
    // fallback ไปตัวไม่รวม VAT เฉพาะกรณี API รุ่นก่อนหน้า (ช่วง deploy คาบเกี่ยว)
    issuableAmount(row) {
      if (!row) return 0;
      const v = row.issuable_amount_incl_vat;
      if (v !== undefined && v !== null && v !== "" && isFinite(Number(v))) return Number(v);
      return Number(row.unbilled_sales_amount) || 0;
    },

    // ── เลือกรายการรับเงิน → ออกใบกำกับภาษี (spec 2026-08-13 รอบบ่าย) ───────────
    // เลือกได้เฉพาะเงินที่ยัง "ออกใบได้จริง" — BE ก็ตีกลับอยู่แล้วแต่ต้องกันตั้งแต่
    // ช่องติ๊กเพื่อไม่ให้ผู้ใช้เสียเวลาไปเจอ error ที่แก้อะไรไม่ได้:
    //   - ผูกใบไปแล้ว → payment_not_eligible (already_linked)
    //   - company_id เป็น NULL (เงินเก่าที่หาบริษัทไม่เจอ — MtConsignmentPaymentList
    //     ตั้งใจไม่กรองออก) → ติ๊กแล้วไม่มีบริษัทให้ส่ง POST จะไปตายที่ BE ด้วย
    //     "company_id is required" (ภาษาโปรแกรมเมอร์ล้วน) หรือ payment_not_eligible
    //     (no_company) ถ้าไปปนกับแถวของบริษัทจริง
    isRowSelectable(row) {
      return !row.invoice_id && !!row.company_id;
    },
    onSelectionChange(rows) {
      this.selectedRows = rows || [];
      // ต้องรู้ "ยอดขายที่ยังออกใบได้" ของบริษัทที่เลือก แม้ตัวกรองจะเป็นบริษัทอื่น/ทุกบริษัท
      if (this.selectedCompanyId && !this.selectedCompanySummary) {
        this.fetchCompanySummary(this.selectedCompanyId);
      }
    },
    clearSelection() {
      this.selectedRows = [];
      if (this.$refs.paymentsTable) this.$refs.paymentsTable.clearSelection();
    },
    async fetchCompanySummary(companyId) {
      try {
        const res = await ModernTradeService.consignmentPaymentsSummary({ company_id: companyId });
        const d = this.parseList(res);
        const rows = (d.items || []).filter(
          s => !this.summaryRows.some(x => Number(x.company_id) === Number(s.company_id))
        );
        if (rows.length) this.summaryRows = this.summaryRows.concat(rows);
      } catch (e) {
        // non-fatal — ตัวเลขเทียบไม่ขึ้นก็ยังกดออกใบได้ (BE เป็นคนตัดสิน)
      }
    },
    async issueSelected() {
      // ปุ่ม disabled อยู่แล้ว — กันไว้อีกชั้นและบอกเหตุผลถ้าถูกเรียกตรง ๆ
      if (!this.isEditModernTrade || this.issuing) return;
      if (this.crossCompanySelection) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.issue_cross_company"), icon: "warning" });
        return;
      }
      if (!this.selectedRows.length) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.issue_need_selection"), icon: "warning" });
        return;
      }
      // ไม่มีบริษัท = ห้ามยิง (POST จะส่ง company_id: null แล้วได้ error ภาษาโปรแกรมเมอร์)
      if (!this.selectedCompanyId) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.no_company_hint"), icon: "warning" });
        return;
      }
      const companyId = this.selectedCompanyId;
      const paymentIds = this.selectedRows.map(r => r.id);
      const amount = this.selectedAmount;
      const result = await swal.fire({
        title: this.$t("modern_trade.consignment_payment.issue_confirm_title"),
        text: this.$t("modern_trade.consignment_payment.issue_confirm", {
          company: this.selectedCompanyName,
          amount: this.formatMoney(amount),
          count: paymentIds.length,
        }),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      // sweetalert2 v9 → result.value (ไม่ใช่ isConfirmed ของ v10+)
      if (!result.value) return;
      this.issuing = true;
      try {
        const res = await ModernTradeService.consignmentInvoiceCreate({
          company_id: companyId,
          payment_ids: paymentIds,
        });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const inv = d.invoice || d || {};
        const docNo = inv.doc_no || (inv.id ? "#" + inv.id : "-");
        const net = Number(inv.net_amount) || amount;
        // BE รายงานสินค้าที่ไม่มีราคาแม้ตอนสำเร็จ — ใบถูกต้อง แต่ยอดขายของสินค้าพวกนั้น
        // ไม่มีวันถูกออกใบจนกว่าจะไปผูก/ตั้งราคา จึงต้องบอกตรงนี้ ไม่ใช่ปล่อยเงียบ
        const okNote = this.unpricedNote(d);
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.issued_title"),
          text: [
            this.$t("modern_trade.consignment_payment.issued", {
              doc_no: docNo,
              amount: this.formatMoney(net),
            }),
            okNote,
          ].filter(Boolean).join(" "),
          icon: "success",
        });
        this.clearSelection();
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showIssueErr(err);
      } finally {
        this.issuing = false;
      }
    },
    // รายชื่อสินค้าที่ยังไม่ผูก MT / ไม่มีราคา — เป็น "ข้อมูล" ไม่ใช่ประโยค
    // (รหัสสินค้า หรือ wms#id) จึงแสดงดิบ ๆ ได้ทุกภาษา
    // ใช้ได้ทั้งตอนสำเร็จ (Data[0]) และตอนถูกปฏิเสธ (Datum)
    unpricedNote(payload) {
      const list = (payload && payload.skipped_unpriced_products) || [];
      const count = Number(payload && payload.skipped_unpriced) || list.length;
      if (!count) return "";
      return this.$t("modern_trade.consignment_payment.skipped_unpriced_note", {
        count: count,
        products: list.length ? list.join(", ") : "-",
      });
    },
    // BE กำลังเพิ่มรหัสใหม่: มีสินค้าที่ยังไม่มีราคา + บริษัทนี้เคยออกใบไปแล้ว
    // → ห้ามออกใบจนกว่าจะตั้งราคา (ไม่งั้นการตั้งราคาย้อนหลังจะไปกินของที่ออกใบไปแล้ว)
    // ตอนเขียนนี้รหัสยังไม่ลง — พอรู้ชื่อจริงเติมใน CODES ได้เลย ระหว่างนี้จับด้วย
    // โครงสร้าง: error ที่ไม่รู้จักแต่แนบ skipped_unpriced_products มา = เคสนี้แน่นอน
    // (insufficient_sales / payment_not_eligible ถูกจับไปก่อนหน้าแล้ว)
    isUnpricedBlocked(code, datum) {
      const CODES = ["unpriced_sales", "unpriced_products", "unpriced_sales_blocked"];
      if (CODES.indexOf(code) !== -1) return true;
      const list = (datum && datum.skipped_unpriced_products) || [];
      return !!(code && list.length);
    },
    // 400 insufficient_sales → ต้องบอก "ยอดสูงสุดที่ออกได้" ให้ผู้ใช้ปรับการเลือกได้
    //
    // สัญญาจาก BE (MT-ConsignmentInvoice.go — r.Set → JSON "Datum" ของ go-core):
    // Datum = { max_amount, max_sub_total, requested_amount, requested_sub_total,
    //           sales_total, invoiced_sub_total, over_invoiced,
    //           skipped_unpriced, skipped_unpriced_products }
    // โดย max_amount เป็นยอด "รวม VAT" จึงเทียบกับเงินที่ผู้ใช้ติ๊กได้ตรง ๆ
    // และเป็นตัวเดียวกับ issuable_amount_incl_vat บนแถบสรุป (เลขต้องตรงกัน)
    maxIssuableFromError(body) {
      const datum = (body && body.Datum) || null;
      if (!datum) return null;
      const v = datum.max_amount;
      if (v === undefined || v === null || v === "" || !isFinite(Number(v))) return null;
      return Number(v);
    },
    showIssueErr(err) {
      const body = (err && err.response && err.response.data) || {};
      const code = body.Msg || "";
      if (code === "insufficient_sales") {
        // เพดาน 0 ห้ามโผล่มาลอย ๆ — BE แยกไว้ 3 กรณีจริง ๆ และ Datum ส่งตัวเลข
        // ที่ใช้ประกอบข้อความมาครบ จึงประกอบเองได้ทั้ง th/en (Cause เป็นไทยล้วน)
        //   1) ยังพอออกได้แต่ไม่พอกับที่เลือก → บอกยอดสูงสุด
        //   2) ยังไม่มียอดขายเลย → ให้ไปบันทึก/นำเข้าข้อมูลการขาย
        //   3) ยอดขายถูกออกใบไปหมดแล้ว (หรือราคาถูกแก้ย้อนหลังจนติดลบ) → ห้ามบอกให้
        //      ไปนำเข้ายอดขาย ต้องชี้ไปที่ใบที่ออกไปแล้ว พร้อมตัวเลขสองตัว
        const datum = body.Datum || null;
        const max = this.maxIssuableFromError(body);
        const parts = [];
        if (!datum) {
          // API รุ่นก่อน: ไม่มี Datum → ข้อความ Cause ระบุยอดไว้ในตัวอยู่แล้ว
          parts.push(body.Cause || this.$t("modern_trade.consignment_payment.insufficient_sales"));
        } else if (max !== null && max > 0) {
          parts.push(this.$t("modern_trade.consignment_payment.insufficient_sales"));
          parts.push(this.$t("modern_trade.consignment_payment.max_issuable", { amount: this.formatMoney(max) }));
        } else if ((Number(datum.sales_total) || 0) <= 0) {
          parts.push(this.$t("modern_trade.consignment_payment.no_sales_yet"));
        } else {
          parts.push(
            this.$t("modern_trade.consignment_payment.over_invoiced", {
              sales: this.formatMoney(datum.sales_total),
              invoiced: this.formatMoney(datum.invoiced_sub_total),
            })
          );
        }
        // สินค้าที่ไม่มีราคาทำให้ทุกตัวเลขข้างบนหดลง — ต่อท้ายเสมอเมื่อมี
        const note = this.unpricedNote(datum);
        if (note) parts.push(note);
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.insufficient_sales_title"),
          text: parts.join(" "),
          icon: "warning",
        });
        // เลขบนการ์ด/แถบสรุปอาจเก่าไปแล้ว — ดึงใหม่ให้ผู้ใช้ปรับการเลือกจากของจริง
        this.loadSummary();
        return;
      }
      if (code === "payment_not_eligible") {
        // Datum { payment_ids, problems:[{payment_id, reason}] } — บอกให้ตรงแถว
        // แปลเหตุผลเองเหมือนสาย insufficient_sales (Cause ของ BE เป็นไทยล้วน)
        const problems = (body.Datum && body.Datum.problems) || [];
        const detail = problems
          .map(p => `#${p.payment_id} — ${this.ineligibleReasonLabel(p.reason)}`)
          .join("\n");
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.payment_not_eligible"),
          text: detail || body.Cause || "",
          icon: "error",
        });
        this.loadList();
        return;
      }
      // ออกใบไม่ได้เพราะยังมีสินค้าที่ไม่ได้ผูก/ไม่มีราคา — บอกว่าต้องไปตั้งราคาก่อน
      // แล้วค่อยออกใบ พร้อมรายชื่อสินค้าที่ต้องแก้
      if (this.isUnpricedBlocked(code, body.Datum)) {
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.unpriced_blocked_title"),
          text: [
            this.$t("modern_trade.consignment_payment.unpriced_blocked"),
            this.unpricedNote(body.Datum),
          ].filter(Boolean).join(" "),
          icon: "warning",
        });
        this.loadSummary();
        return;
      }
      this.showErr(err);
    },
    // เหตุผลรายแถวจาก BE (MT-ConsignmentInvoice.go mtPayIneligible*)
    // เขียนคีย์เต็มเพื่อให้ grep/เครื่องมือตรวจ i18n เจอ (ห้ามต่อสตริงคีย์)
    ineligibleReasonLabel(reason) {
      switch (reason) {
        case "already_linked": return this.$t("modern_trade.consignment_payment.reason_already_linked");
        case "wrong_company": return this.$t("modern_trade.consignment_payment.reason_wrong_company");
        case "wrong_payment_type": return this.$t("modern_trade.consignment_payment.reason_wrong_payment_type");
        case "no_company": return this.$t("modern_trade.consignment_payment.reason_no_company");
        case "not_found": return this.$t("modern_trade.consignment_payment.reason_not_found");
        case "bad_amount": return this.$t("modern_trade.consignment_payment.reason_bad_amount");
        default: return reason || "";
      }
    },

    // ── บันทึกรับเงิน ───────────────────────────────────────────────────────────
    openRecord() {
      if (!this.isEditModernTrade) return;
      this.recordForm.company_id = this.filters.company_id || null;
      this.recordForm.paid_date = this.today();
      this.showRecordDialog = true;
    },
    resetRecordForm() {
      this.recordForm = {
        company_id: null,
        amount: null,
        paid_date: null,
        method: "transfer",
        note: "",
        slip_object_key: "",
        slip_cdn_url: "",
      };
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    // ตัวเลขเทียบของบริษัทใน dialog — ถ้ายังไม่มีใน summaryRows (เพราะกรองบริษัทอื่นอยู่)
    // ให้ดึงเฉพาะบริษัทนั้นมาเติม
    async loadRecordCompare() {
      const id = this.recordForm.company_id;
      if (!id) return;
      if (this.summaryRows.some(s => Number(s.company_id) === Number(id))) return;
      // non-fatal — การ์ดเทียบไม่ขึ้นก็บันทึกได้ (ไม่มีเพดาน)
      await this.fetchCompanySummary(id);
    },
    async submitRecord() {
      if (!this.isEditModernTrade) return;
      if (!this.recordForm.company_id) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.company_required"), icon: "warning" });
        return;
      }
      const amount = Number(this.recordForm.amount);
      if (!amount || !isFinite(amount) || amount <= 0) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.amount_required"), icon: "warning" });
        return;
      }
      if (!this.recordForm.paid_date) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.date_required"), icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        await ModernTradeService.consignmentPaymentCreate({
          company_id: this.recordForm.company_id,
          amount: amount,
          paid_date: this.recordForm.paid_date,
          method: this.recordForm.method,
          note: this.recordForm.note,
          slip_object_key: this.recordForm.slip_object_key || null,
        });
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.recorded"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.showRecordDialog = false;
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.saving = false;
      }
    },

    // ── สลิป (presign เดิม object_type = mt_payment) ────────────────────────────
    clearSlip() {
      this.recordForm.slip_object_key = "";
      this.recordForm.slip_cdn_url = "";
      if (this.$refs.slipInput) this.$refs.slipInput.value = "";
    },
    slipUrl(key) {
      if (!key) return "";
      if (/^https?:\/\//i.test(key)) return key;
      return "https://cdn.madamefin.com/" + String(key).replace(/^\/+/, "");
    },
    async onSlipPicked(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      const okType = ["image/jpeg", "image/png", "image/webp", "application/pdf"].indexOf(file.type) !== -1;
      if (!okType) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_bad_type"), icon: "warning" });
        this.clearSlip();
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        swal.fire({ title: this.$t("modern_trade.payment.slip_too_big"), icon: "warning" });
        this.clearSlip();
        return;
      }
      this.slipUploading = true;
      try {
        const presignRes = await ModernTradeService.slipPresign({
          file_name: file.name,
          content_type: file.type,
        });
        const d = (presignRes.data && presignRes.data.Data && presignRes.data.Data[0]) || {};
        if (!d.upload_url || !d.object_key) throw new Error("presign failed");
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", d.upload_url);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error("Upload failed: " + xhr.status));
          };
          xhr.onerror = () => reject(new Error("Network error"));
          xhr.send(file);
        });
        this.recordForm.slip_object_key = d.object_key;
        this.recordForm.slip_cdn_url = d.cdn_url || this.slipUrl(d.object_key);
      } catch (err) {
        const detail = err && err.message ? ": " + err.message : "";
        swal.fire({ title: this.$t("modern_trade.payment.slip_upload_failed") + detail, icon: "error" });
        this.clearSlip();
      } finally {
        this.slipUploading = false;
      }
    },

    // ── เมนูรายแถว (ผูก / ปลด / ลบ) — ทั้งคอลัมน์ซ่อนเมื่อไม่มีสิทธิ์เขียน ──────
    onRowCommand(cmd, row) {
      if (!this.isEditModernTrade) return;
      // ด่านสองของปุ่มผูกใบ (เมนูปิดไว้แล้ว) — ผูกไม่ได้จริงเพราะไม่มีบริษัท
      if (cmd === "link" && !row.company_id) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.no_company_hint"), icon: "warning" });
        return;
      }
      if (cmd === "link") this.openLink(row);
      else if (cmd === "unlink") this.handleUnlink(row);
      else if (cmd === "delete") this.handleDelete(row);
    },
    // โหลดใบกำกับที่ผูกได้ตอนเปิด dialog — el-select remote ไม่ยิงตอนกางรายการ
    // (Element UI 2.4.11) จึง prefetch ของบริษัทนั้นแทน
    //
    // MtInvoiceList (MT-Invoice.go:504) clamp per_page > 100 กลับเป็น 50 — ขอ 200
    // จึงได้ 50 แถวล่าสุด ซึ่งอาจเป็นใบขายขาดล้วนจนกรองแล้วเหลือศูนย์ทั้งที่มีใบฝากขาย
    // ค้างอยู่จริง. ใช้ค่าสูงสุดที่ BE ยอม (100) + ยิงแยกตามสถานะที่ผูกได้
    // (1 ยังไม่รับเงิน / 2 รับบางส่วน) เพื่อให้ทั้ง 2 หน้าเป็นใบที่ยังรับเงินไม่ครบล้วน
    // — BE ไม่มีตัวกรอง source จึงยังต้องกรอง source=2 ฝั่ง FE
    async openLink(row) {
      this.linkPayment = row;
      this.linkInvoiceId = null;
      this.linkInvoices = [];
      this.showLinkDialog = true;
      this.linkLoading = true;
      try {
        const base = { company_id: row.company_id, per_page: 100, page: 1 };
        const [unpaidRes, partialRes] = await Promise.all([
          ModernTradeService.invoicesList(Object.assign({ status: 1 }, base)),
          ModernTradeService.invoicesList(Object.assign({ status: 2 }, base)),
        ]);
        const items = (this.parseList(unpaidRes).items || []).concat(this.parseList(partialRes).items || []);
        // ผูกได้เฉพาะใบฝากขาย (source 2) ที่ยังไม่ยกเลิกและยังรับเงินไม่ครบ
        this.linkInvoices = items
          .filter(iv => iv.source === 2 && iv.status !== 9 && this.invoiceRemaining(iv) > 0)
          .sort((a, b) => Number(b.id) - Number(a.id));
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.linkLoading = false;
      }
    },
    resetLinkForm() {
      this.linkPayment = null;
      this.linkInvoices = [];
      this.linkInvoiceId = null;
    },
    invoiceRemaining(iv) {
      const net = Number(iv.net_amount) || 0;
      const paid = Number(iv.paid_amount) || 0;
      return Math.max(net - paid, 0);
    },
    invoiceOptionLabel(iv) {
      return `${iv.doc_no || "#" + iv.id} — ${this.$t("modern_trade.consignment_payment.invoice_remaining")} ${this.formatMoney(this.invoiceRemaining(iv))}`;
    },
    async submitLink() {
      if (!this.isEditModernTrade || !this.linkPayment) return;
      if (!this.linkInvoiceId) {
        swal.fire({ title: this.$t("modern_trade.consignment_payment.invoice_select_required"), icon: "warning" });
        return;
      }
      this.linking = true;
      try {
        await ModernTradeService.paymentLink(this.linkPayment.id, { invoice_id: this.linkInvoiceId });
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.linked"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.showLinkDialog = false;
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      } finally {
        this.linking = false;
      }
    },
    async handleUnlink(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.consignment_payment.unlink_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      // sweetalert2 v9 → result.value
      if (!result.value) return;
      try {
        await ModernTradeService.paymentLink(row.id, { invoice_id: null });
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.unlinked"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      }
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.consignment_payment.delete_confirm"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("close") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.paymentDelete(row.id);
        swal.fire({
          title: this.$t("modern_trade.consignment_payment.deleted"),
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
        this.loadSummary();
        this.loadList();
      } catch (err) {
        this.showErr(err);
      }
    },

    // ── utils ───────────────────────────────────────────────────────────────────
    // BE ส่งโค้ดข้อผิดพลาดใน Msg (payment_exceeds / wrong_payment_type) — แปลให้อ่านออก
    showErr(err) {
      const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
      let title = this.errMsg(err);
      if (code === "payment_exceeds") title = this.$t("modern_trade.consignment_payment.exceeds");
      else if (code === "wrong_payment_type") title = this.$t("modern_trade.consignment_payment.wrong_type");
      // เงินเก่าที่ไม่มีบริษัท (เมนูปิดไว้แล้ว เหลือกรณี race/ยิงตรง) — Cause ของ BE
      // เป็นไทยล้วน จึงต้องมีข้อความของหน้าจอเอง
      else if (code === "payment_no_company") title = this.$t("modern_trade.consignment_payment.no_company_hint");
      // แถวถูกแก้ไปแล้วระหว่างที่หน้าจอยังถืออันเก่า — BE ตั้งใจให้ client ลองใหม่
      // (ไม่ใช่ edge case) ข้อความจึงต้องบอก "ต้องทำอะไรต่อ" ไม่ใช่บอกว่าเกิดอะไรขึ้น
      else if (code === "state_changed") title = this.$t("modern_trade.consignment_payment.state_changed");
      swal.fire({ title, icon: "error" });
    },
    methodLabel(m) {
      switch (m) {
        case "cash": return this.$t("modern_trade.payment.method_cash");
        case "transfer": return this.$t("modern_trade.payment.method_transfer");
        case "cheque": return this.$t("modern_trade.payment.method_cheque");
        case "other": return this.$t("modern_trade.payment.method_other");
        default: return m || "-";
      }
    },
    today() {
      const d = new Date();
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
    formatMoney(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    errMsg(err) {
      return (err && err.response && err.response.data && err.response.data.Cause) || (err && err.message) || "Error";
    },
  },
};
</script>

<style scoped>
/* ปุ่มค้นหาสูงเท่า el-input/el-select (40px) เหมือนหน้าใบกำกับภาษี */
.mt-csg-search-btn {
  height: 40px;
}

/* แถบสรุปการเลือก — แยกตัวเองออกจากตัวกรองด้านบนและตารางด้านล่าง */
.mt-csg-selection-bar {
  padding-top: 0.75rem;
  padding-bottom: 0.5rem;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fe;
}

.mt-csg-selection-figures {
  font-size: 0.875rem;
}

/* Argon .badge = font-size 66% + uppercase → เล็กเกินอ่านไม่ออกบนตาราง */
.mt-csg-badge {
  display: inline-block;
  padding: 0.35rem 0.6rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.2;
  text-transform: none;
  white-space: nowrap;
  border-radius: 0.375rem;
}
</style>
